import { Col, Row } from 'antd';
import React, { useMemo } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { type ModulesOptionsDataFragment } from '../../../api/fragments/ModulesOptionsData';
import ApplicationChart from './ApplicationChart';
import { useDashboardContext } from './DashboardContext';
import DepositPaymentsPerDay from './DepositPaymentsPerDay';
import DisplayControl from './DisplayControl';
import LatestApplicationTable from './LatestApplicationTable';
import LeadChart from './LeadChart';
import PopularApplicationVariantChart from './PopularApplicationVariantChart';
import PopularLeadEventChart from './PopularLeadEventChart';
import PopularLeadVariantChart from './PopularLeadVariantChart';
import Statistics from './Statistics';
import TopSalesManWithApplication from './TopSalesManWithApplication';
import TopSalesManWithLead from './TopSalesManWithLead';
import TopSalesManWithLoan from './TopSalesManWithLoan';

export type DashboardContentProps = {
    items: Array<ModulesOptionsDataFragment>;
};

const DashboardContent = ({ items }: DashboardContentProps) => {
    const {
        state: { visible },
    } = useDashboardContext();

    const sectionItems = useMemo(() => {
        const top = [
            visible?.applicationChart && <ApplicationChart />,
            visible?.popularApplicationVariantChart && <PopularApplicationVariantChart />,
            visible?.topSalesManWithApplication && <TopSalesManWithApplication />,
            visible?.topSalesManWithLoan && <TopSalesManWithLoan />,
        ].filter(Boolean);

        const bottom = [
            visible?.popularLeadEventChart && <PopularLeadEventChart />,
            visible?.leadChart && <LeadChart />,
            visible?.popularLeadVariantChart && <PopularLeadVariantChart />,
            visible?.depositPaymentsPerDay && <DepositPaymentsPerDay />,
            visible?.topSalesManWithLead && <TopSalesManWithLead />,
        ].filter(Boolean);

        // combine top and bottom section when the latestApplicationTable in the middle is not shown
        const combined = visible?.latestApplicationTable ? [] : [...top, ...bottom];

        return { top, bottom, combined };
    }, [visible]);

    return (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                <DisplayControl />
            </Col>

            <Statistics />

            {!visible?.latestApplicationTable ? (
                <Col span={24}>
                    <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 992: 2 }}>
                        <Masonry gutter="16px">{sectionItems.combined}</Masonry>
                    </ResponsiveMasonry>
                </Col>
            ) : (
                <>
                    {sectionItems.top?.length > 0 && (
                        <Col span={24}>
                            <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 992: 2 }}>
                                <Masonry gutter="16px">{sectionItems.top}</Masonry>
                            </ResponsiveMasonry>
                        </Col>
                    )}

                    <LatestApplicationTable />

                    {sectionItems.bottom?.length > 0 && (
                        <Col span={24}>
                            <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 992: 2 }}>
                                <Masonry gutter="16px">{sectionItems.bottom}</Masonry>
                            </ResponsiveMasonry>
                        </Col>
                    )}
                </>
            )}
        </Row>
    );
};

export default DashboardContent;
